/**
 * QrScanner — in-app camera QR scanner for pairing (2026-08-30).
 *
 * WHY THIS EXISTS: on iOS an installed (home-screen) web app gets its own
 * storage container AND the camera app hands a scanned link to **Safari**,
 * never to the installed app. So the dashboard's QR pairs Safari and leaves
 * the installed app empty, with no way in short of typing a 16-hex-character
 * subdomain on a touch keyboard. Scanning in-app is the only route that
 * keeps "scan it and you're done" true where the operator actually works.
 *
 * iOS specifics that are load-bearing, not incidental (scouted 2026-08-30):
 *  - getUserMedia in standalone mode was broken for ~2 years (WebKit
 *    #185448) and is **RESOLVED FIXED since iOS 13.4**. Stale blog posts
 *    still say it cannot work; they are wrong on modern iPadOS.
 *  - The video element MUST carry `playsinline` (+ muted/autoplay) or iOS
 *    takes the stream fullscreen instead of rendering it inline.
 *  - A camera stream is dropped by `history.pushState`/`replaceState`
 *    (WebKit fixed this only in 17.4). ConnectPage strips `?bridge=` via
 *    replaceState, so nothing here may touch history while the stream is
 *    live — we stop the camera BEFORE handing the result back.
 *  - getUserMedia requires a secure context and a user gesture. The button
 *    that opens this modal is the gesture; if `mediaDevices` is missing we
 *    say why rather than showing a dead viewfinder.
 *
 * The camera is released on every exit path (success, cancel, unmount,
 * error) — a still-live capture indicator after closing reads as a bug and
 * drains battery on a gig rig.
 *
 * 2026-10-18, operator on a reset iPad: "i allowed my camera but the camera
 * doesnt start and just sits there". In an installed iOS app `video.play()`
 * can stay PENDING forever after the app was closed and reopened, until the
 * iPad restarts (WebKit 252465) - and this modal awaited it before marking
 * itself ready or starting the decoder, so it sat on "Starting camera"
 * with no error. Now: ready means a FRAME arrived (never "play() settled"),
 * a stall after STALL_MS says so and offers Try again, and "Take a photo
 * instead" decodes a still from the system camera (a file input), which
 * does not go through the live video pipeline at all.
 */

import { createSignal, onCleanup, onMount, Show } from 'solid-js';
import { Portal } from 'solid-js/web';
import jsQR from 'jsqr';
import { shellPortal } from '../layout/shellPortal.ts';
import { extractBridgeUrl, looksLikeBridgeUrl } from '../util/pairingLink.ts';

/** How often to run a decode pass. Every animation frame is wasteful and
 *  makes older iPads warm; ~8/s still feels instant to a human aiming a
 *  camera at a static code on a screen. */
const DECODE_INTERVAL_MS = 120;

/** No frame by now = the live camera is stuck, not slow. A healthy iPad
 *  shows its first frame well under a second after permission. */
const STALL_MS = 4000;

/** Longest edge a photo is decoded at. A 12 MP still is too big for jsQR to
 *  be quick and too fine for its binariser; a QR filling part of the frame
 *  reads at any of these. Tried in order until one decodes. */
const PHOTO_EDGES = [1600, 1000, 2400];

export function QrScanner(props: {
  /** Called with the extracted BRIDGE url (not the pairing link) once a
   *  usable code is decoded. The camera is already stopped when this runs. */
  /** Called with the bridge URL, plus the RAW scanned payload.
   *  The raw string is passed because a pairing link can carry more than the
   *  URL - notably the WebTransport advert that arms the offline path - and
   *  extracting only the URL here would silently discard it. */
  onFound: (bridgeUrl: string, raw: string) => void;
  onClose: () => void;
}) {
  const [error, setError] = createSignal<string | null>(null);
  const [ready, setReady] = createSignal(false);
  /** Set when a code decoded but was not a pairing link — otherwise aiming
   *  at some unrelated QR looks identical to the scanner being broken. */
  const [wrongCode, setWrongCode] = createSignal(false);
  /** The live camera produced no frame within STALL_MS. */
  const [stalled, setStalled] = createSignal(false);
  const [photoBusy, setPhotoBusy] = createSignal(false);
  /** A photo was taken but no QR could be read from it. */
  const [photoMiss, setPhotoMiss] = createSignal(false);
  /** The live camera was released so the system camera could take a photo. */
  const [paused, setPaused] = createSignal(false);

  let videoEl: HTMLVideoElement | undefined;
  let canvasEl: HTMLCanvasElement | undefined;
  let fileEl: HTMLInputElement | undefined;
  let stream: MediaStream | null = null;
  let timer: ReturnType<typeof setInterval> | null = null;
  let stallTimer: ReturnType<typeof setTimeout> | null = null;
  let done = false;

  function stopCamera(): void {
    if (timer !== null) { clearInterval(timer); timer = null; }
    if (stallTimer !== null) { clearTimeout(stallTimer); stallTimer = null; }
    stream?.getTracks().forEach((t) => t.stop());
    stream = null;
    if (videoEl) videoEl.srcObject = null;
  }

  /** A frame has really arrived - the only honest meaning of "ready". */
  function frameArrived(): void {
    if (!videoEl || videoEl.videoWidth === 0) return;
    setReady(true);
    setStalled(false);
    if (stallTimer !== null) { clearTimeout(stallTimer); stallTimer = null; }
  }

  /** Decode a QR from a decoded image, trying a few working sizes. */
  function decodeImage(src: CanvasImageSource, iw: number, ih: number): string | null {
    if (!canvasEl) return null;
    const ctx = canvasEl.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    for (const edge of PHOTO_EDGES) {
      const s = Math.min(1, edge / Math.max(iw, ih));
      const w = Math.max(1, Math.round(iw * s));
      const h = Math.max(1, Math.round(ih * s));
      canvasEl.width = w;
      canvasEl.height = h;
      ctx.drawImage(src, 0, 0, w, h);
      const found = jsQR(ctx.getImageData(0, 0, w, h).data, w, h, { inversionAttempts: 'attemptBoth' });
      if (found?.data) return found.data;
    }
    return null;
  }

  async function onPhoto(file: File | undefined): Promise<void> {
    if (!file || done) return;
    setPhotoBusy(true);
    setPhotoMiss(false);
    setWrongCode(false);
    try {
      let data: string | null = null;
      if (typeof createImageBitmap === 'function') {
        const bmp = await createImageBitmap(file);
        data = decodeImage(bmp, bmp.width, bmp.height);
        bmp.close();
      } else {
        const url = URL.createObjectURL(file);
        try {
          const img = new Image();
          img.src = url;
          await img.decode();
          data = decodeImage(img, img.naturalWidth, img.naturalHeight);
        } finally { URL.revokeObjectURL(url); }
      }
      if (!data) { setPhotoMiss(true); return; }
      if (!looksLikeBridgeUrl(data)) { setWrongCode(true); return; }
      finish(extractBridgeUrl(data), data);
    } catch {
      setPhotoMiss(true);
    } finally {
      setPhotoBusy(false);
      if (fileEl) fileEl.value = '';
    }
  }

  function takePhoto(): void {
    // Release the live camera first: iOS gives the camera to one user, and
    // the system camera the file input opens would otherwise fight it.
    stopCamera();
    setReady(false);
    setStalled(false);
    setPaused(true);
    fileEl?.click();
  }

  function finish(bridgeUrl: string, raw: string): void {
    if (done) return;
    done = true;
    // Stop BEFORE the callback: the caller navigates / mutates history, and
    // on iOS < 17.4 that kills a live stream in a way that can wedge the
    // camera until the app is force-quit.
    stopCamera();
    props.onFound(bridgeUrl, raw);
  }

  function close(): void {
    stopCamera();
    props.onClose();
  }

  function tick(): void {
    if (done || !videoEl || !canvasEl) return;
    const w = videoEl.videoWidth;
    const h = videoEl.videoHeight;
    if (!w || !h) return; // first frames arrive before dimensions settle
    if (!ready()) frameArrived();
    const ctx = canvasEl.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;
    canvasEl.width = w;
    canvasEl.height = h;
    ctx.drawImage(videoEl, 0, 0, w, h);
    const { data } = ctx.getImageData(0, 0, w, h);
    // `attemptBoth` also reads light-on-dark codes — a QR shown on a
    // dark-themed dashboard is exactly our case.
    const found = jsQR(data, w, h, { inversionAttempts: 'attemptBoth' });
    if (!found?.data) return;
    if (!looksLikeBridgeUrl(found.data)) {
      setWrongCode(true);
      return;
    }
    finish(extractBridgeUrl(found.data), found.data);
  }

  /** Start (or restart) the live camera. Called from mount and from the
   *  Try again button - the latter is a user gesture, which iOS likes for
   *  both getUserMedia and play(). getUserMedia is called before any await,
   *  so the gesture is still live when it runs. */
  async function startCamera(): Promise<void> {
    stopCamera();
    setReady(false);
    setStalled(false);
    setPaused(false);
    setPhotoMiss(false);
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('This browser will not give the app a live camera. Take a photo of the QR instead, or use the pairing link.');
      return;
    }
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false,
      });
      if (done) { s.getTracks().forEach((t) => t.stop()); return; } // closed while permission was pending
      stream = s;
      // A track iOS ends or mutes (another app took the camera, the app was
      // backgrounded) leaves a frozen frame; say so rather than sit there.
      s.getVideoTracks().forEach((t) => t.addEventListener('ended', () => { if (!done) { setReady(false); setStalled(true); } }));
      if (videoEl) {
        videoEl.srcObject = s;
        // NOT awaited: in an installed iOS app play() can stay pending
        // forever (see the header). Readiness comes from frames instead.
        void videoEl.play().catch(() => { /* autoplay policy - the stall offer covers it */ });
      }
      timer = setInterval(tick, DECODE_INTERVAL_MS);
      stallTimer = setTimeout(() => { stallTimer = null; if (!done && !ready()) setStalled(true); }, STALL_MS);
    } catch (err) {
      const name = (err as { name?: string } | null)?.name ?? '';
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        setError('Camera access was denied. Allow the camera for this app in Settings and try again, or take a photo of the QR instead.');
      } else if (name === 'NotFoundError' || name === 'OverconstrainedError') {
        setError('No camera found on this device. Use the pairing link instead.');
      } else {
        setError(`Could not start the live camera${name ? ` (${name})` : ''}. Take a photo of the QR instead, or use the pairing link.`);
      }
    }
  }

  onMount(() => { void startCamera(); });

  onCleanup(() => { done = true; stopCamera(); });
  function onKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') close();
  }
  onMount(() => window.addEventListener('keydown', onKey));
  onCleanup(() => window.removeEventListener('keydown', onKey));

  return (
    <Portal mount={shellPortal() ?? document.body}>
      <div class="qr-scan-overlay" role="dialog" aria-modal="true" aria-label="Scan the pairing QR" onClick={(e) => { if (e.target === e.currentTarget) close(); }}>
        <div class="qr-scan-modal" data-testid="qr-scanner">
          <div class="qr-scan-title">Scan the pairing QR</div>

          <Show when={!error()}>
            <div class="qr-scan-viewport">
              {/* playsinline + muted + autoplay: without these iOS hijacks
                  the stream into the fullscreen player. Set as attributes
                  (not just properties) so they apply on first paint. */}
              <video
                ref={videoEl}
                class="qr-scan-video"
                playsinline
                muted
                autoplay
                onLoadedData={frameArrived}
                onPlaying={frameArrived}
              />
              <div class="qr-scan-reticle" aria-hidden="true" />
              <Show when={!ready()}>
                <div class="qr-scan-hint" data-state={photoBusy() ? 'reading' : paused() ? 'paused' : stalled() ? 'stalled' : 'starting'}>
                  {photoBusy() ? 'Reading the photo...'
                    : paused() ? 'Live camera paused for the photo. Tap Try again to go back to it.'
                    : stalled() ? 'The live camera did not start. Take a photo of the QR instead, or try again.'
                    : 'Starting camera...'}
                </div>
              </Show>
            </div>
            <div class="qr-scan-sub">
              On the computer running OPBridge, open its dashboard → Pairing → <strong>"Pair a new device"</strong>, and point this camera at the QR.
            </div>
            <Show when={wrongCode()}>
              <div class="qr-scan-warn">
                That code isn't an OPBridge pairing link. Make sure you're scanning the QR from the Pairing panel.
              </div>
            </Show>
          </Show>

          <Show when={error()}>
            <div class="qr-scan-error">{error()}</div>
          </Show>

          <Show when={photoMiss()}>
            <div class="qr-scan-warn">
              No QR code found in that photo. Fill most of the photo with the QR and hold still, then take another.
            </div>
          </Show>

          <canvas ref={canvasEl} style="display:none" />
          {/* The system camera, not getUserMedia: works when the live
              camera is stuck (see the header). */}
          <input
            ref={fileEl}
            type="file"
            accept="image/*"
            capture="environment"
            style="display:none"
            data-testid="qr-photo-input"
            onChange={(e) => { void onPhoto(e.currentTarget.files?.[0]); }}
          />

          <div class="qr-scan-actions">
            <Show when={(stalled() || paused()) && !error()}>
              <button type="button" class="settings-secondary" onClick={() => { void startCamera(); }}>Try again</button>
            </Show>
            <button type="button" class="settings-secondary" disabled={photoBusy()} onClick={takePhoto}>Take a photo instead</button>
            <button type="button" class="settings-secondary" onClick={close}>Cancel</button>
          </div>
        </div>
      </div>
    </Portal>
  );
}
